import {
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Table,
  TableColumnType,
} from "antd";
import { debounce } from "lodash";
import { useCallback, useState } from "react";
import {
  IQuerySuppliesLogistics,
  ISuppliesLogistics,
} from "../../models/Logistics";
import {
  useCreateSupplierLogistics,
  useDeleteSupplierLogistics,
  useGetSupplierLogistics,
  useUpdateSupplierLogistics,
} from "../../query/LogisticsQuery";
import { notify } from "../../utils/toast";

const SupplierLogistics = () => {
  const [query, setQuery] = useState<IQuerySuppliesLogistics>({
    page: 1,
    limit: 10,
    internalCode: undefined,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ISuppliesLogistics | null>(
    null,
  );
  const [form] = Form.useForm();

  const { data, isLoading } = useGetSupplierLogistics(query);
  const createMutation = useCreateSupplierLogistics();
  const updateMutation = useUpdateSupplierLogistics();
  const deleteMutation = useDeleteSupplierLogistics();

  const handleSearch = useCallback(
    debounce((value: string) => {
      setQuery((prev) => ({
        ...prev,
        internalCode: value || undefined,
        page: 1,
      }));
    }, 300),
    [],
  );

  const handleOpenModal = (record?: ISuppliesLogistics) => {
    if (record) {
      setEditingRecord(record);
      form.setFieldsValue(record);
    } else {
      setEditingRecord(null);
      form.resetFields();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingRecord) {
        await updateMutation.mutateAsync({
          id: editingRecord.id,
          data: values,
          query: query,
        });
        notify.success("Cập nhật thành công");
      } else {
        await createMutation.mutateAsync(values);
        notify.success("Thêm mới thành công");
      }
      handleCloseModal();
    } catch (error) {
      notify.error("Có lỗi xảy ra");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMutation.mutateAsync({
        id,
        query: query,
      });
      notify.success("Xóa thành công");
    } catch (error) {
      notify.error("Xóa thất bại");
    }
  };

  const columns: TableColumnType<ISuppliesLogistics>[] = [
    {
      title: "Mã nội bộ",
      dataIndex: "internalCode",
      key: "internalCode",
      width: 150,
      fixed: "left",
    },
    {
      title: "Mã phụ",
      dataIndex: "secondaryCode",
      key: "secondaryCode",
      width: 120,
    },
    {
      title: "Mã phụ mở rộng",
      dataIndex: "secondaryCodeExt",
      key: "secondaryCodeExt",
      width: 150,
    },
    {
      title: "Part Number",
      dataIndex: "partNumber",
      key: "partNumber",
      width: 150,
    },
    {
      title: "Nhà sản xuất",
      dataIndex: "manufacturer",
      key: "manufacturer",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
      width: 250,
      ellipsis: true,
    },
    {
      title: "Đơn vị",
      dataIndex: "unit",
      key: "unit",
      width: 100,
      align: "center",
    },
    {
      title: "Thuế nhập khẩu (%)",
      dataIndex: "importTax",
      key: "importTax",
      width: 150,
      align: "right",
      render: (value: number) => `${value}%`,
    },
    {
      title: "Thao tác",
      key: "action",
      width: 120,
      fixed: "right",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          />
          <Popconfirm
            title="Xóa nhà cung cấp"
            description="Bạn có chắc chắn muốn xóa?"
            onConfirm={() => handleDelete(record.id)}
            okText="Xác nhận"
            cancelText="Hủy"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div className="mb-4">
        <Input
          placeholder="Tìm kiếm theo mã nội bộ"
          prefix={<SearchOutlined />}
          allowClear
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 300 }}
        />
        <Button
          type="primary"
          onClick={() => handleOpenModal()}
          className="ml-2"
        >
          Thêm mới
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={data?.data || []}
        rowKey="id"
        loading={isLoading}
        scroll={{ x: 1200 }}
        pagination={{
          current: query.page,
          pageSize: query.limit,
          total: data?.pagination.total || 0,
          showSizeChanger: true,
          showTotal: (total) => `Tổng ${total} bản ghi`,
          onChange: (page, pageSize) => {
            setQuery({ ...query, page, limit: pageSize });
          },
        }}
      />

      <Modal
        title={editingRecord ? "Chỉnh sửa nhà cung cấp" : "Thêm nhà cung cấp"}
        open={isModalOpen}
        onCancel={handleCloseModal}
        onOk={handleSubmit}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width={800}
      >
        <Form form={form} layout="vertical" className="mt-4">
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label={
                <span>
                  Mã nội bộ <span className="text-red-500">*</span>
                </span>
              }
              name="internalCode"
              rules={[{ required: true, message: "Vui lòng nhập mã nội bộ" }]}
            >
              <Input placeholder="Nhập mã nội bộ" />
            </Form.Item>
            <Form.Item label="Mã phụ" name="secondaryCode">
              <Input placeholder="Nhập mã phụ" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item label="Mã phụ mở rộng" name="secondaryCodeExt">
              <Input placeholder="Nhập mã phụ mở rộng" />
            </Form.Item>
            <Form.Item
              label={
                <span>
                  Part Number <span className="text-red-500">*</span>
                </span>
              }
              name="partNumber"
              rules={[{ required: true, message: "Vui lòng nhập part number" }]}
            >
              <Input placeholder="Nhập part number" />
            </Form.Item>
          </div>

          <Form.Item
            label={
              <span>
                Nhà sản xuất <span className="text-red-500">*</span>
              </span>
            }
            name="manufacturer"
            rules={[{ required: true, message: "Vui lòng nhập nhà sản xuất" }]}
          >
            <Input placeholder="Nhập nhà sản xuất" />
          </Form.Item>

          <Form.Item
            label={
              <span>
                Mô tả <span className="text-red-500">*</span>
              </span>
            }
            name="description"
            rules={[{ required: true, message: "Vui lòng nhập mô tả" }]}
          >
            <Input.TextArea rows={3} placeholder="Nhập mô tả" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label={
                <span>
                  Đơn vị <span className="text-red-500">*</span>
                </span>
              }
              name="unit"
              rules={[{ required: true, message: "Vui lòng nhập đơn vị" }]}
            >
              <Input placeholder="Nhập đơn vị (VD: cái, kg, m)" />
            </Form.Item>
            <Form.Item
              label={
                <span>
                  Thuế nhập khẩu (%) <span className="text-red-500">*</span>
                </span>
              }
              name="importTax"
              rules={[
                { required: true, message: "Vui lòng nhập thuế nhập khẩu" },
              ]}
            >
              <InputNumber
                min={0}
                max={100}
                placeholder="Nhập thuế nhập khẩu"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierLogistics;
